import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AuthSession, Kullanici, Firma, Sube } from '@novamuhasebe/shared'
import { apiClient } from '@/services/api.client'
import * as Sentry from '@sentry/react'

export type FirmaSecenegi = {
  id: string
  ad: string
  rol: string
  subeAd: string | null
}

interface AuthState {
  kullanici: Kullanici | null
  firma: Firma | null
  sube: Sube | null
  accessToken: string | null
  refreshToken: string | null
  permissions: string[]
  isAuthenticated: boolean
  isLoading: boolean

  // Çoklu firma seçimi için bekleyen durum
  pendingFirmalar: FirmaSecenegi[] | null
  pendingEmail: string | null
  pendingSifre: string | null

  // Giriş sonrası kullanılabilir firmalar (switcher için)
  availableFirmalar: FirmaSecenegi[]

  // Şube seçimi
  availableSubeler: Sube[]
  switchSube: (subeId: string) => Promise<void>

  login: (email: string, sifre: string) => Promise<{ firmaSec: true; firmalar: FirmaSecenegi[] } | void>
  loginFirmaSec: (firmaId: string) => Promise<void>
  switchFirm: (firmaId: string) => Promise<void>
  clearPending: () => void
  logout: () => void
  setSession: (session: AuthSession) => void
  hasPermission: (permission: string) => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      kullanici: null,
      firma: null,
      sube: null,
      accessToken: null,
      refreshToken: null,
      permissions: [],
      isAuthenticated: false,
      isLoading: false,
      pendingFirmalar: null,
      pendingEmail: null,
      pendingSifre: null,
      availableFirmalar: [],
      availableSubeler: [],

      setSession: (session: AuthSession) => {
        const sessionAny = session as any
        set({
          kullanici: session.kullanici,
          firma: session.firma,
          sube: session.sube,
          accessToken: session.accessToken,
          refreshToken: session.refreshToken,
          permissions: session.permissions,
          isAuthenticated: true,
          availableSubeler: sessionAny.availableSubeler ?? [],
        })
        Sentry.setUser({ id: session.kullanici.id, email: session.kullanici.email, firmaId: session.kullanici.firmaId } as any)
      },

      login: async (email: string, sifre: string) => {
        set({ isLoading: true })
        try {
          const res = await apiClient.post('/auth/login', { email, sifre }) as {
            data: AuthSession | { firmaSec: true; firmalar: FirmaSecenegi[] }
          }

          if ('firmaSec' in res.data && res.data.firmaSec) {
            // Çoklu firma — seçim gerekiyor
            set({
              pendingFirmalar: res.data.firmalar,
              pendingEmail: email,
              pendingSifre: sifre,
            })
            return { firmaSec: true, firmalar: res.data.firmalar }
          }

          // Tek firma — direkt giriş
          const session = res.data as AuthSession & { availableFirmalar?: FirmaSecenegi[] }
          get().setSession(session)
          set({
            availableFirmalar: session.availableFirmalar ?? (session.firma ? [{
              id: session.firma.id,
              ad: session.firma.ad,
              rol: session.kullanici.rol,
              subeAd: session.sube?.ad ?? null,
            }] : []),
          })
        } finally {
          set({ isLoading: false })
        }
      },

      loginFirmaSec: async (firmaId: string) => {
        const { pendingEmail, pendingSifre, pendingFirmalar } = get()
        if (!pendingEmail || !pendingSifre) return

        set({ isLoading: true })
        try {
          const res = await apiClient.post('/auth/login/firma-sec', {
            email: pendingEmail,
            sifre: pendingSifre,
            firmaId,
          }) as { data: AuthSession & { availableFirmalar?: FirmaSecenegi[] } }

          get().setSession(res.data)
          // Yanıtta availableFirmalar varsa onu kullan, yoksa pendingFirmalar'ı kullan
          const available = res.data.availableFirmalar ?? pendingFirmalar ?? []
          set({
            availableFirmalar: available,
            pendingFirmalar: null,
            pendingEmail: null,
            pendingSifre: null,
          })
        } finally {
          set({ isLoading: false })
        }
      },

      switchSube: async (subeId: string) => {
        set({ isLoading: true })
        try {
          const res = await apiClient.post('/auth/switch-sube', { subeId }) as {
            data: AuthSession & { availableSubeler?: Sube[] }
          }
          get().setSession(res.data)
        } finally {
          set({ isLoading: false })
        }
      },

      switchFirm: async (firmaId: string) => {
        set({ isLoading: true })
        try {
          const res = await apiClient.post('/auth/switch-firm', { firmaId }) as {
            data: AuthSession & { availableFirmalar?: FirmaSecenegi[] }
          }
          get().setSession(res.data)
          if (res.data.availableFirmalar) {
            set({ availableFirmalar: res.data.availableFirmalar })
          }
        } finally {
          set({ isLoading: false })
        }
      },

      clearPending: () => {
        set({ pendingFirmalar: null, pendingEmail: null, pendingSifre: null })
      },

      logout: () => {
        apiClient.post('/auth/logout').catch(() => {})
        Sentry.setUser(null)
        set({
          kullanici: null, firma: null, sube: null,
          accessToken: null, refreshToken: null,
          permissions: [], isAuthenticated: false,
          pendingFirmalar: null, pendingEmail: null, pendingSifre: null,
          availableFirmalar: [], availableSubeler: [],
        })
      },

      hasPermission: (permission: string) => {
        const { permissions, kullanici } = get()
        if (kullanici?.rol === 'super_admin' || kullanici?.rol === 'admin') return true
        return permissions.includes(permission)
      },
    }),
    {
      name: 'nova-auth',
      partialize: (state) => ({
        kullanici: state.kullanici,
        firma: state.firma,
        sube: state.sube,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        permissions: state.permissions,
        isAuthenticated: state.isAuthenticated,
        availableFirmalar: state.availableFirmalar,
        availableSubeler: state.availableSubeler,
      }),
    }
  )
)
